import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/components/AppShell";
import { useModuleFeature } from "@/components/ModuleGate";
import { Button } from "@/components/ui";
import { api, ApiError, type OrderDetail } from "@/lib/api";
import { intlLocale, t, msg } from "@/lib/i18n";

const ADDRESS_REASON = (r: string) => r.startsWith("Adresse prüfen: ") || r === "Lieferadresse unvollständig" || r === "In Prüfung bei Logiful (Adresse)";
const dt = (iso: string | null) => (iso ? new Date(iso).toLocaleString(intlLocale(), { dateStyle: "short", timeStyle: "short" }) : "");

/** Adressklärung beim Endkunden: Stand und „Kunden fragen“ (nur bei Adressproblemen, mit Modul Kundenkommunikation) */
export function AddressRequestBox({ order }: { order: Pick<OrderDetail, "id" | "status" | "holdReasons" | "shippingAddress"> }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const licensed = useModuleFeature("customer_comms", "address_clarification");
  const relevant = order.status === "on_hold" && order.holdReasons.some(ADDRESS_REASON);
  const q = useQuery({ queryKey: ["address-request", tenantId, order.id], queryFn: () => api.orders.addressRequest(tenantId, order.id), enabled: licensed });
  const ask = useMutation({
    mutationFn: () => api.orders.requestAddress(tenantId, order.id),
    onSuccess: () => { toast.success("Kunde wird angeschrieben", { description: msg("Die Mail geht innerhalb einer Minute raus.") }); void qc.invalidateQueries({ queryKey: ["address-request", tenantId, order.id] }); },
    onError: (e) => toast.error("Nicht angefragt", { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "" }),
  });
  if (!licensed) return null;
  const c = q.data?.clarification;
  const lastMail = [...(q.data?.messages ?? [])].reverse().find((m) => m.kind !== "delay_notice");
  if (!relevant && !c) return null;
  return (
    <div className="mt-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-[12.5px] flex flex-wrap items-center gap-2">
      {c?.status === "corrected" || c?.status === "confirmed" ? (
        <span className="flex items-center gap-1.5"><CheckCircle2 className="size-4 text-success" /> {t("Kunde hat die Adresse")} {c.status === "corrected" ? t("korrigiert") : t("bestätigt")} ({dt(c.resolvedAt)})</span>
      ) : c?.status === "open" ? (
        <span className="flex items-center gap-1.5"><Mail className="size-4 text-fg-subtle" /> {t("Kunde angefragt")}{lastMail ? ` · ${lastMail.status === "sent" ? `gesendet ${dt(lastMail.sentAt)}` : lastMail.status === "failed" ? "Versand fehlgeschlagen" : "wird gesendet"}` : ""} {t("· wartet auf Antwort")}</span>
      ) : (
        <span className="text-fg-muted">{order.shippingAddress.email ? t("Euer Kunde kann die Adresse selbst prüfen und korrigieren.") : t("Keine E-Mail-Adresse des Kunden vorhanden.")}</span>
      )}
      {relevant && order.shippingAddress.email && c?.status !== "corrected" && c?.status !== "confirmed" && (
        <Button size="sm" className="ml-auto" icon={<Send className="size-3.5" />} loading={ask.isPending} onClick={() => ask.mutate()}>{c?.status === "open" ? t("Erneut fragen") : t("Kunden fragen")}</Button>
      )}
    </div>
  );
}
